import React, { useEffect, useMemo } from 'react'
import styles from './KioskStatus.module.css';
import CloseIcon1 from '../../svgIcons/CloseIcon1'
import DateIcon from '../../svgIcons/DateIcon';
import KeyLockerIcon from '../../svgIcons/KeyLockerIcon';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, IRootState } from '../../../redux/rootReducer';
import { getKioskStatus } from '../../../redux/kiosk-status/actions/kiosk-status.action';
import Loader from '../../common/loader/Loader';
import moment from 'moment';


interface KioskStatusProps {
    locationId: string;
    locationName: string;
    setShowModal: (isOpen: boolean) => void;
}

type LockerState = 'capturing' | 'missing' | 'idle';

interface LockerStatusItem {
    lockerNumber: number;
    status: string;
    statusText?: string;
    subText?: string;
    totalAccesses?: number;
    failedAccesses?: number;
    lastAccessTime?: string | null;
}

const statusContent: Record<LockerState, { title: string; description: string; }> = {
    capturing: {
        title: 'Code Verifying',
        description: '& Capturing Images',
    },
    missing: {
        title: 'Code Verifying',
        description: 'But No Image Captured',
    },
    idle: {
        title: 'Not In Use',
        description: 'No Data Available',

    },
};

const StatusBadge = ({ state }: { state: LockerState }) => {
    return (
        <span className={`${styles.lockerStatusBadge} ${styles[state]}`} aria-hidden="true" />
    );
};

const normalizeLockerState = (status?: string): LockerState => {
    const normalizedStatus = status?.toLowerCase();

    if (normalizedStatus === 'failing' || normalizedStatus === 'failed' || normalizedStatus === 'missing') {
        return 'missing';
    }

    if (normalizedStatus === 'unused' || normalizedStatus === 'idle' || normalizedStatus === 'not_in_use') {
        return 'idle';
    }

    return 'capturing';
};

const stripWrappedQuotes = (value?: string) => value?.replace(/^"+|"+$/g, '').trim();

const getLockerStatusCopy = (locker: LockerStatusItem, state: LockerState) => {
    if (!locker.statusText) return statusContent[state];

    if (locker.statusText.includes(' But ')) {
        const [title, ...descriptionParts] = locker.statusText.split(' But ');
        return {
            title,
            description: `But ${descriptionParts.join(' But ')}`,
        };
    }

    if (locker.statusText.includes(',')) {
        const [title, ...descriptionParts] = locker.statusText.split(',');
        return {
            title: title.trim(),
            description: descriptionParts.join(',').trim() || locker.subText || statusContent[state].description,
        };
    }

    if (locker.statusText.includes(' & ')) {
        const [title, ...descriptionParts] = locker.statusText.split(' & ');
        return {
            title,
            description: `& ${descriptionParts.join(' & ')}`,
        };
    }

    return {
        title: locker.statusText,
        description: locker.subText || statusContent[state].description,
    };
};

const KioskStatus = ({ locationId, locationName, setShowModal }: KioskStatusProps) => {
    const dispatch: AppDispatch = useDispatch();
    const { data: kioskStatus, errorMsg, loading } = useSelector((state: IRootState) => state.kioskStatusReducer);
    const endDate = useMemo(() => moment().format('YYYY-MM-DD'), []);
    const startDate = useMemo(() => moment().subtract(7, 'days').format('YYYY-MM-DD'), []);

    useEffect(() => {
        if (locationId) {
            dispatch(getKioskStatus(locationId, startDate, endDate));
        }
    }, [dispatch, endDate, locationId, startDate]);

    const lockers: LockerStatusItem[] = useMemo(() => {
        return Array.isArray(kioskStatus?.lockers) ? kioskStatus.lockers : [];
    }, [kioskStatus]);

    const propertyName = stripWrappedQuotes(kioskStatus?.propertyName) || locationName || `Location ${locationId}`;
    const lockersCount = kioskStatus?.lockersCount || lockers.length;

    return (<>
        {loading && <Loader />}
        <div className={`modal-container ${styles.kioskStatusModal}`}>

            <div className={styles.modalHeader}>
                <div>
                    <h3>Kiosk Status</h3>
                    {propertyName && <p className={styles.kioskStatusSubtitle}>{propertyName}</p>}
                </div>
                <div className={styles.closeIcon} onClick={() => { setShowModal(false) }}>
                    <CloseIcon1 />
                </div>
            </div>
            <div className={styles.modalBodyContainer}>
                {/* <div className={styles.kioskStatusToolbar}>
                    <button className={styles.dateRangeBtn} type="button">
                        <DateIcon fill="#10b981" />
                        <span>Last Week</span>
                        <strong>({moment(startDate).format('MM/DD/YYYY')} - {moment(endDate).format('MM/DD/YYYY')})</strong>
                    </button>
                </div> */}

                <section className={styles.lockerStatusPanel}>
                    {!loading && <h4>Locker Status ({lockersCount} Lockers)</h4>}

                    {errorMsg ? (
                        <div className={styles.kioskStatusMessage}>{errorMsg}</div>
                    ) : lockers.length > 0 ? (
                        <div className={styles.lockerStatusGrid}>
                            {lockers.map((locker) => {
                                const state = normalizeLockerState(locker.status);
                                const status = getLockerStatusCopy(locker, state);

                                return (
                                    <article className={`${styles.lockerCard} ${styles[state]}`} key={locker.lockerNumber}>
                                        <div className={styles.lockerCardHeader}>
                                            <h5>Locker {locker.lockerNumber}</h5>
                                            <span className={`${styles.lockerKeyIcon} ${styles[state]}`} aria-hidden="true">
                                                <KeyLockerIcon size={22} color="currentColor" />
                                            </span>
                                        </div>

                                        <div className={styles.lockerCardBody}>
                                            <StatusBadge state={state} />
                                            <p>
                                                {status.title}
                                                <span>{status.description}</span>
                                            </p>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    ) : (!loading &&
                        <div className={styles.kioskStatusMessage}>No locker status data available</div>
                    )}

                    <div className={styles.lockerStatusLegend}>
                        <div className={styles.legendItem}>
                            <StatusBadge state="capturing" />
                            <div>
                                <strong>Code Verifying & Capturing Images</strong>
                                <span>All functions working well</span>
                            </div>
                        </div>
                        <div className={styles.legendItem}>
                            <StatusBadge state="missing" />
                            <div>
                                <strong>Code Verifying But No Image Captured</strong>
                                <span>Image not capturing</span>
                            </div>
                        </div>
                        <div className={styles.legendItem}>
                            <StatusBadge state="idle" />
                            <div>
                                <strong>Not In Use</strong>
                                <span>No data available</span>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    </>
    )
}

export default KioskStatus
